import { useState } from 'react';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d66qkqej853lk6gjuidg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Successfully subscribed!');
        setEmail('');
      } else {
        setSubmitMessage('Subscription failed. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="w-full bg-white py-10 px-12">
        <div className="flex items-center">
          <img 
            src="https://public.readdy.ai/ai/img_res/c256a438-8b81-4ff0-af0e-e1b8548db102.png" 
            alt="ENA d.o.o. Logo" 
            className="h-12 w-auto mr-4"
          />
          <h1 className="text-3xl font-bold text-gray-900">ENA d.o.o.</h1>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="w-full bg-white py-24 px-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-6">iOS Development Experts</p>
          <h2 className="text-6xl font-serif font-bold text-gray-900 leading-tight mb-8">
            Modern Applications<br />for iOS Platform
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            We are a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="w-full bg-[#F8F6F3] py-24 px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* VPN Product Card */}
            <div className="bg-[#2A2A2A] rounded-3xl p-10 min-h-[400px] flex flex-col">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-shield-line text-white text-5xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">VPN Solution</h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Secure your connection and protect your privacy with our advanced VPN technology. Browse safely, access content freely, and keep your data encrypted across all your iOS devices.
              </p>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-[#FEFDFB] rounded-3xl p-10 min-h-[400px] flex flex-col relative">
              <div className="absolute top-6 left-6 bg-teal-500 text-white text-xs px-3 py-1 rounded-full flex items-center whitespace-nowrap">
                <i className="ri-sparkling-line mr-1"></i>
                Optimized
              </div>
              <div className="flex-1 flex items-center justify-center my-8">
                <div className="w-48 h-48 flex items-center justify-center">
                  <i className="ri-delete-bin-line text-gray-800 text-9xl"></i>
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">Cleaner</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Optimize your device storage and enhance performance with intelligent cleaning algorithms. Remove unnecessary files, manage photos, and keep your iOS device running smoothly.
              </p>
            </div>

            {/* Protection Suite Card */}
            <div className="bg-[#F5F5F5] rounded-3xl p-10 min-h-[400px] flex flex-col relative">
              <div className="absolute top-6 left-6 bg-teal-500 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
                Enhanced Security
              </div>
              <div className="flex-1 flex items-center justify-center my-8">
                <div className="w-48 h-48 flex items-center justify-center">
                  <i className="ri-lock-line text-gray-800 text-9xl"></i>
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">Protection Suite</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Comprehensive device protection combining multiple security layers. Monitor threats, secure your data, and maintain complete control over your mobile device privacy and safety.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="w-full bg-white py-28 px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-20">
            <div className="md:col-span-2">
              <p className="text-xs uppercase tracking-wider text-gray-400">About ENA d.o.o.</p>
            </div>
            <div className="md:col-span-3">
              <p className="text-2xl text-gray-700 leading-relaxed">
                Our team specializes in creating innovative iOS applications that prioritize user experience and security. With years of expertise in mobile development, we focus on delivering high-quality solutions that meet the evolving needs of modern users. Every product we build reflects our commitment to excellence, performance, and user-centric design principles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <footer id="contact" className="w-full bg-[#2B2B2B] py-20 px-16">
        <div className="max-w-7xl mx-auto">
          {/* Top Row */}
          <div className="flex justify-between items-center mb-16">
            <div className="flex items-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/c256a438-8b81-4ff0-af0e-e1b8548db102.png" 
                alt="ENA d.o.o. Logo" 
                className="h-8 w-auto mr-3"
              />
              <div className="text-xl font-bold text-white">ENA d.o.o.</div>
            </div>
            <div className="text-base text-gray-400">Get in touch with our team</div>
          </div>

          {/* Middle Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-16 mb-20">
            {/* Location Column */}
            <div>
              <h4 className="text-lg font-bold text-white mb-5">Location</h4>
              <div className="text-base text-gray-400 leading-relaxed space-y-2">
                <p><strong className="text-gray-300">Address:</strong> Jurja Haulika 20a</p>
                <p><strong className="text-gray-300">City:</strong> Karlovac</p>
                <p><strong className="text-gray-300">Zip:</strong> 47000</p>
                <p><strong className="text-gray-300">Country:</strong> Croatia</p>
              </div>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-lg font-bold text-white mb-5">Contact</h4>
              <div className="text-base text-gray-400 leading-relaxed space-y-2">
                <p><strong className="text-gray-300">Email:</strong> contact@enadoo.com</p>
                <p><strong className="text-gray-300">Number:</strong> +1 6016546204</p>
              </div>
            </div>

            {/* Company Info Column */}
            <div>
              <h4 className="text-lg font-bold text-white mb-5">Company</h4>
              <div className="text-base text-gray-400 leading-relaxed space-y-2">
                <p>iOS Development</p>
                <p>Mobile Security</p>
                <p>App Optimization</p>
                <p>Technical Support</p>
              </div>
            </div>

            {/* Copyright Column */}
            <div className="text-right">
              <div className="text-2xl font-serif text-white mb-2">© ENA d.o.o. 2025</div>
              <div className="text-sm text-gray-400 mb-6">All rights reserved</div>
            </div>
          </div>

          {/* Bottom Visual Area */}
          <div className="grid grid-cols-1 md:grid-cols-10 gap-10 items-center">
            {/* Left Product Scene */}
            <div className="md:col-span-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/1c5e610454b801b2f0431e227fe963de.jpg" 
                alt="iOS workspace" 
                className="w-full h-60 object-cover rounded-3xl"
              />
            </div>

            {/* Middle Tagline */}
            <div className="md:col-span-4 pl-10">
              <h3 className="text-4xl font-serif text-white leading-tight">
                Innovative iOS<br />
                Solutions for<br />
                Modern Users
              </h3>
            </div>

            {/* Right Newsletter Area */}
            <div className="md:col-span-3">
              <p className="text-sm text-gray-400 mb-5 leading-relaxed">
                Stay updated with our latest releases and features
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-3" data-readdy-form id="newsletter-form">
                <input 
                  type="email" 
                  name="email"
                  placeholder="Your email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 h-12 px-4 bg-transparent border border-[#404040] rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-500"
                  required
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 px-8 bg-teal-500 hover:bg-teal-600 text-white rounded-2xl whitespace-nowrap flex items-center transition-colors cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Subscribe'}
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </form>
              {submitMessage && (
                <p className={`mt-3 text-sm ${submitMessage.includes('Success') ? 'text-teal-400' : 'text-red-400'}`}>
                  {submitMessage}
                </p>
              )}
            </div>
          </div>

          {/* Quick Link */}
          <div className="mt-16 pt-8 border-t border-gray-700 text-center">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-gray-500 hover:text-gray-400 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
